/**
 * WebAudio 저수준 합성기.
 * 외부 음원 파일 없이 효과음과 BGM 을 전부 코드로 만든다.
 */

import { AUDIO } from '../data/balance';

export interface ToneOptions {
  freq: number;
  /** 글리산도 목표 주파수 (생략 시 고정) */
  endFreq?: number;
  type?: OscillatorType;
  duration: number;
  gain?: number;
  attack?: number;
  /** 시작 지연 (초) */
  delay?: number;
  detune?: number;
}

export interface NoiseOptions {
  duration: number;
  gain?: number;
  filterFreq?: number;
  endFilterFreq?: number;
  q?: number;
  type?: BiquadFilterType;
  delay?: number;
}

export class Synth {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private sfxBus: GainNode | null = null;
  private bgmBus: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private muted = false;

  /** 브라우저 정책 때문에 첫 사용자 입력 이후에 호출해야 한다. */
  start(): void {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') void this.ctx.resume();
      return;
    }
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;

    const ctx = new Ctor();
    const master = ctx.createGain();
    master.gain.value = AUDIO.masterVolume;
    master.connect(ctx.destination);

    const sfxBus = ctx.createGain();
    sfxBus.gain.value = AUDIO.sfxVolume;
    sfxBus.connect(master);

    const bgmBus = ctx.createGain();
    bgmBus.gain.value = AUDIO.bgmVolume;
    bgmBus.connect(master);

    // 화이트 노이즈 버퍼 (타격/스윙용)
    const length = Math.floor(ctx.sampleRate * 0.6);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;

    this.ctx = ctx;
    this.master = master;
    this.sfxBus = sfxBus;
    this.bgmBus = bgmBus;
    this.noiseBuffer = buffer;
  }

  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  get currentTime(): number {
    return this.ctx?.currentTime ?? 0;
  }

  get sfxOut(): GainNode | null {
    return this.sfxBus;
  }

  get bgmOut(): GainNode | null {
    return this.bgmBus;
  }

  get context(): AudioContext | null {
    return this.ctx;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.master) {
      this.master.gain.value = this.muted ? 0 : AUDIO.masterVolume;
    }
    return this.muted;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  tone(opts: ToneOptions, bus: GainNode | null = this.sfxBus): void {
    const ctx = this.ctx;
    if (!ctx || !bus) return;

    const t0 = ctx.currentTime + (opts.delay ?? 0);
    const osc = ctx.createOscillator();
    osc.type = opts.type ?? 'sine';
    osc.frequency.setValueAtTime(opts.freq, t0);
    if (opts.endFreq !== undefined) {
      osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.endFreq), t0 + opts.duration);
    }
    if (opts.detune) osc.detune.setValueAtTime(opts.detune, t0);

    const env = ctx.createGain();
    const peak = opts.gain ?? 0.3;
    const attack = opts.attack ?? 0.005;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(peak, t0 + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);

    osc.connect(env);
    env.connect(bus);
    osc.start(t0);
    osc.stop(t0 + opts.duration + 0.02);
  }

  noise(opts: NoiseOptions, bus: GainNode | null = this.sfxBus): void {
    const ctx = this.ctx;
    if (!ctx || !bus || !this.noiseBuffer) return;

    const t0 = ctx.currentTime + (opts.delay ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noiseBuffer;

    const filter = ctx.createBiquadFilter();
    filter.type = opts.type ?? 'bandpass';
    filter.Q.value = opts.q ?? 1.2;
    filter.frequency.setValueAtTime(opts.filterFreq ?? 1200, t0);
    if (opts.endFilterFreq !== undefined) {
      filter.frequency.exponentialRampToValueAtTime(
        Math.max(40, opts.endFilterFreq),
        t0 + opts.duration,
      );
    }

    const env = ctx.createGain();
    const peak = opts.gain ?? 0.25;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(peak, t0 + 0.004);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.duration);

    src.connect(filter);
    filter.connect(env);
    env.connect(bus);
    src.start(t0);
    src.stop(t0 + opts.duration + 0.02);
  }

  /** 여러 음을 순차적으로 재생 */
  sequence(
    freqs: readonly number[],
    step: number,
    opts: Omit<ToneOptions, 'freq' | 'duration' | 'delay'> & { noteDuration?: number },
    bus: GainNode | null = this.sfxBus,
  ): void {
    freqs.forEach((f, i) => {
      this.tone(
        {
          ...opts,
          freq: f,
          duration: opts.noteDuration ?? step * 1.6,
          delay: i * step,
        },
        bus,
      );
    });
  }
}

export const synth = new Synth();
